import { intl } from '@beelday/common'
import { Config } from 'common/config'
import { useDisableFeatureOnAudioStreamPlaying } from 'features/audio-streaming/use-disable-feature-on-audio-stream-playing'
import { useDisableFeatureOnYouTubeStreamPlaying } from 'features/youtube-streaming/use-disable-feature-on-youtube-stream-playing'
import { selectCurrentUserMuted } from 'interaction-scheme/redux'
import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
	MenuToggleItem,
	PopupMenu,
	PopupMenuElement,
	PopupNestedMenu,
} from 'room/common/presentation/menu'
import { selectEnabledFeatures } from 'room/common/redux'
import { useAssertedJoinedRoomAddress } from 'room/common/use-joined-room-address'
import { useAssertedWorkflowUser } from 'room/common/use-workflow-user'
import useIsTrainer from 'room/common/use-is-trainer'
import ToolBlockedHint from 'room/common/presentation/tool-blocked-hint'
import { useMediaDevices } from 'utils/hooks/use-media-devices'
import useMicApi from './microphone-api'
import MicrophoneIcon from './microphone-icon'
import MicrophoneMutedIcon from './microphone-muted-icon'

const MicMenuItem: React.FC = () => {
	const { availableMicrophones, selectedMicrophone, selectMicrophone } =
		useMediaDevices()

	const api = useMicApi(Config.beeldayBackendUrl)
	const address = useAssertedJoinedRoomAddress()
	const user = useAssertedWorkflowUser()
	const enabledFeatures = useSelector(selectEnabledFeatures)

	const isFeatureEnabled = enabledFeatures.includes('MicrophoneType')
	const isMuteMic = useSelector(selectCurrentUserMuted)
	const disabledOnAudioStreamPlaying = useDisableFeatureOnAudioStreamPlaying()
	const disabledOnYouTubeStreamPlaying =
		useDisableFeatureOnYouTubeStreamPlaying()
	const disabled =
		disabledOnAudioStreamPlaying || disabledOnYouTubeStreamPlaying
	const isTrainer = useIsTrainer()

	const toggleMic = useCallback(() => {
		if (!isMuteMic) {
			api.muteMic(address, user.id)
		} else {
			api.unmuteMic(address, user.id)
		}
	}, [isMuteMic, api, address, user])

	const currentMic = availableMicrophones.find(
		mic => mic.deviceId === selectedMicrophone
	)

	if (!isFeatureEnabled) return null
	return (
		<MenuToggleItem
			active={isMuteMic}
			activeIcon={<MicrophoneMutedIcon />}
			icon={<MicrophoneIcon />}
			onClick={toggleMic}
			label={<intl.Translate>room_menu.microphone</intl.Translate>}
			disabled={disabled}
			disabledHint={
				isTrainer ? (
					<ToolBlockedHint tool="media" action="microphone" />
				) : (
					<intl.Translate>room_menu.hint.microphone_media</intl.Translate>
				)
			}
		>
			{currentMic ? (
				<PopupMenu>
					<PopupMenuElement text={currentMic.label}>
						<PopupNestedMenu>
							<>
								{availableMicrophones.map(mic => (
									<PopupMenuElement
										key={mic.deviceId}
										text={mic.label}
										active={selectedMicrophone === mic.deviceId}
										onClick={() => selectMicrophone(mic.deviceId)}
									/>
								))}
							</>
						</PopupNestedMenu>
					</PopupMenuElement>
				</PopupMenu>
			) : null}
		</MenuToggleItem>
	)
}

export default MicMenuItem
